import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, Subscription, timer, catchError, map, of, take, tap } from 'rxjs';

import { ApiResponse } from '@models/base-response.model';

import { LoadingService } from './loading.service';
import { SESSION_RESETTABLE } from './session-resettable';
import { ToastService } from './toast.service';
import { CheckOTPRequest, ForgotPasswordRequest, ResetPasswordRequest } from '../api';
import { LoggerService } from './logger.service';
import { environment } from '../../environments/environment';
import {
  UserInfo,
  LoginRequest,
  LoginResponse,
  AppRole,
  AppPermission,
  ROLE_PERMISSIONS,
  RefreshTokenResponse,
  ForceLogoutOptions,
} from '../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  private readonly logger = inject(LoggerService);
  private readonly toastService = inject(ToastService);
  private readonly sessionResettables = inject(SESSION_RESETTABLE, { optional: true }) ?? [];

  private readonly headersApp_Json = new HttpHeaders().set('Content-Type', 'application/json');
  private readonly rememberMeKey = 'rememberMe';
  private readonly loginRoute = '/pages/login';
  private refreshSubscription?: Subscription;

  private readonly _userInfo = signal<UserInfo | null>(this.parseJwt(localStorage.getItem('currentUser') ?? ''));
  public readonly userInfo = this._userInfo.asReadonly();

  private readonly _rememberMe = signal<boolean>(localStorage.getItem(this.rememberMeKey) === 'true');
  public readonly rememberMe = this._rememberMe.asReadonly();

  public readonly isLogged = computed(() => !!this.userInfo());
  public readonly isLogged$ = toObservable(this.isLogged);

  public readonly currentRole = computed(() => {
    const info = this.userInfo();
    if (!info) return null;
    const role = Array.isArray(info.role) ? (info.role[0] ?? null) : info.role;
    return role as AppRole | null;
  });

  public readonly permissions = computed<AppPermission[]>(() => {
    const role = this.currentRole();
    if (!role) return [];
    return ROLE_PERMISSIONS[role] || [];
  });

  constructor() {
    this.purgeExpiredSession();

    if (this.isLogged()) {
      this.startRefreshTokenTimer();
    }
  }

  hasPermission(permission: AppPermission): boolean {
    return this.permissions().includes(permission);
  }

  hasAnyPermission(permissions: AppPermission[]): boolean {
    const userPerms = this.permissions();
    return permissions.some((p) => userPerms.includes(p));
  }

  hasAllPermissions(permissions: AppPermission[]): boolean {
    const userPerms = this.permissions();
    return permissions.every((p) => userPerms.includes(p));
  }

  login(loginRequest: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(environment.baseUrl + 'user/login', loginRequest, { headers: this.headersApp_Json })
      .pipe(tap(() => this.updateAuthState()));
  }

  updateAuthState(): void {
    const token = localStorage.getItem('currentUser');
    this._userInfo.set(token ? this.parseJwt(token) : null);

    if (this.isLogged()) {
      this.startRefreshTokenTimer();
    } else {
      this.stopRefreshTokenTimer();
    }
  }

  persistSessionTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('currentUser', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('tokenExpiration', this.computeTokenExpiration().toISOString());
    this.updateAuthState();
  }

  clearSession(): void {
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('refreshToken');
    this._rememberMe.set(false);
    this.updateAuthState();
    this.resetSessionScopedState();
  }

  purgeExpiredSession(): void {
    const hasStoredSession =
      !!localStorage.getItem('currentUser') ||
      !!localStorage.getItem('refreshToken') ||
      !!localStorage.getItem('tokenExpiration');

    if (hasStoredSession && !this.checkcurrentTokenValidity()) {
      this.clearSession();
    }
  }

  forceLogout(options: ForceLogoutOptions = {}): void {
    const {
      message = 'Per favore, effettua di nuovo il login.',
      title = 'Sessione scaduta',
      redirectDelayMs = 0,
      showToast = true,
      redirect = true,
      enableHtml = false,
    } = options;

    this.clearSession();

    const onLoginPage = this.isLoginRoute();

    if (showToast && !onLoginPage) {
      this.toastService.error(message, title, { enableHtml, source: 'auth' });
    }

    if (redirect && !onLoginPage) {
      if (redirectDelayMs > 0) {
        timer(redirectDelayMs)
          .pipe(take(1))
          .subscribe(() => {
            void this.router.navigate([this.loginRoute]);
          });
      } else {
        void this.router.navigate([this.loginRoute]);
      }
    }
  }

  private resetSessionScopedState(): void {
    const resettables = Array.isArray(this.sessionResettables) ? this.sessionResettables : [this.sessionResettables];
    for (const resettable of resettables) {
      try {
        resettable.resetSessionState();
      } catch (err) {
        this.logger.error('Failed to reset session state', err);
      }
    }
  }

  logout(): void {
    this.clearSession();
    void this.router.navigate([this.loginRoute]);
  }

  checkOTP(OTPRequest: CheckOTPRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + 'user/loginotp', OTPRequest, {
      headers: this.headersApp_Json,
    });
  }

  checkToken(): Observable<boolean> {
    const hasRefresh = !!localStorage.getItem('refreshToken');
    const isRemembered = this.rememberMe();

    if (hasRefresh && isRemembered) {
      if (this.checkcurrentTokenValidity()) {
        return of(true);
      }
      return this.refreshToken(true);
    }

    return of(this.checkcurrentTokenValidity());
  }

  checkcurrentTokenValidity(): boolean {
    const token = localStorage.getItem('currentUser');
    const expiration = localStorage.getItem('tokenExpiration');
    if (!token || !expiration) return false;

    return new Date(expiration) > new Date();
  }

  refreshToken(_force: boolean = false): Observable<boolean> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return of(false);

    this.loadingService.disableLoad();
    return this.http
      .get<RefreshTokenResponse>(
        `${environment.baseUrl}User/RefreshAccessToken?refreshToken=${refreshToken}`,
        { headers: this.headersApp_Json },
      )
      .pipe(
        map((res) => {
          this.loadingService.enableLoad();
          if (res.success) {
            this.persistSessionTokens(res.data.accessToken, res.data.refreshToken);
            return true;
          }
          return false;
        }),
        catchError((err) => {
          this.loadingService.enableLoad();
          this.logger.error('Refresh token failed:', err);
          return of(false);
        }),
      );
  }

  private computeTokenExpiration(): Date {
    const expiration = new Date();
    expiration.setTime(expiration.getTime() + environment.tokenDuration * 60 * 1000);
    return expiration;
  }

  private isLoginRoute(): boolean {
    return this.router.url.startsWith(this.loginRoute);
  }

  private startRefreshTokenTimer(): void {
    this.stopRefreshTokenTimer();
    const delayMs = (environment.tokenDuration - 1) * 60 * 1000;
    this.refreshSubscription = timer(delayMs).subscribe(() => {
      this.refreshToken(true).subscribe();
    });
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = undefined;
    }
  }

  setRememberMe(rememberMe: boolean): void {
    localStorage.setItem(this.rememberMeKey, rememberMe.toString());
    this._rememberMe.set(rememberMe);
  }

  forgotPassword(payload: ForgotPasswordRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + 'User/ResetPassword', payload, {
      headers: this.headersApp_Json,
    });
  }

  resetPswd(request: ResetPasswordRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + 'reset-password', request, {
      headers: this.headersApp_Json,
    });
  }

  parseJwt(token: string): UserInfo | null {
    if (!token || !token.includes('.')) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => {
            const codePoint = c.codePointAt(0);
            return '%' + ('00' + (codePoint ? codePoint.toString(16) : '00')).slice(-2);
          })
          .join(''),
      );
      return JSON.parse(jsonPayload) as UserInfo;
    } catch (err) {
      this.logger.error('Error parsing JWT:', err);
      return null;
    }
  }
}
